"use client";
import React, { useState, useCallback } from "react";
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Typography,
  UploadFile,
  message,
} from "antd";
import { isValidPhoneNumber } from "libphonenumber-js";
import { Service } from "@/lib/api/types";
import { EditionJson } from "@/lib/api";
import PhoneInput from "../PhoneInput";
import Uploader from "./components/Uploader";

const { TextArea } = Input;
const { Paragraph } = Typography;

interface IRequestFormProps {
  userId: string;
  userName: string;
  services: Service[];
  onClose: () => void;
}

const RequestForm: React.FC<IRequestFormProps> = ({
  userId,
  userName,
  services,
  onClose,
}) => {
  const [values, setValues] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    userPhotos: [] as UploadFile[],
    referencePhotos: [] as UploadFile[],
    note: "",
    userId,
  });

  const {
    firstName,
    lastName,
    phoneNumber,
    userPhotos,
    referencePhotos,
    note,
  } = values;

  const [isLoading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setValues((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const onUploadChange = useCallback(
    (type: "userPhotos" | "referencePhotos", fileList: UploadFile[]) => {
      setValues((prev) => ({ ...prev, [type]: fileList }));
    },
    []
  );

  const onSendRequest = useCallback(async () => {
    if (!isValidPhoneNumber(phoneNumber)) {
      messageApi.error("Invalid phone number");
      return;
    }

    setLoading(true);

    try {
      await EditionJson.sendServiceRequest({
        ...values,
        serviceIds: services.map((service) => service._id),
        userPhotos: userPhotos
          .filter((file) => file.response?.url)
          .map((file) => file.response!.url),
        referencePhotos: referencePhotos
          .filter((file) => file.response?.url)
          .map((file) => file.response!.url),
      });

      setSent(true);
      messageApi.success("Request sent");
    } catch (e: any) {
      messageApi.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [phoneNumber, values, services]);

  if (sent) {
    return (
      <>
        {contextHolder}
        <Paragraph>{`Your request has been forwarded to ${userName}. We'll get back to you shortly.`}</Paragraph>
        <Divider />
        <Button onClick={onClose}>Close</Button>
      </>
    );
  }

  return (
    <>
      {contextHolder}
      <Paragraph>
        {`${userName} requires a mini consultation before booking to determine the service duration and price, as these may vary depending on your specific needs. Please submit the form, and ${userName} will reach out to you shortly:`}
      </Paragraph>
      <Paragraph>
        <ul style={{ listStyleType: "square", color: "black" }}>
          {services.map((service) => (
            <li key={service.title}>{service.title}</li>
          ))}
        </ul>
      </Paragraph>
      <Divider dashed />
      <Form layout="vertical" labelWrap={false}>
        {/* User Photos */}
        <Form.Item label="Your current hair photos">
          <Paragraph type="secondary">
            It is important to provide clear photos of your current hair to
            understand your hair type and condition.
          </Paragraph>
          <Uploader
            fileList={userPhotos}
            setFileList={(list) => onUploadChange("userPhotos", list)}
          />
        </Form.Item>

        {/* Reference Photos */}
        <Form.Item label="Reference photos">
          <Paragraph type="secondary">
            Provide photos of the desired style to help the stylist understand
            your preferences.
          </Paragraph>
          <Uploader
            fileList={referencePhotos}
            setFileList={(list) => onUploadChange("referencePhotos", list)}
          />
        </Form.Item>

        {/* Describe Request */}
        <Form.Item label="Describe your request" htmlFor="note">
          <TextArea
            id="note"
            name="note"
            value={note}
            onChange={onChange}
            placeholder="Please describe the service you're seeking. Specify the color, style, and any other specific preferences or requirements that the stylist should be aware of:"
            rows={4}
          />
        </Form.Item>

        {/* First & Last Name */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="First name" htmlFor="firstName">
              <Input
                id="firstName"
                name="firstName"
                value={firstName}
                onChange={onChange}
                placeholder="First Name"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Last name" htmlFor="lastName">
              <Input
                id="lastName"
                name="lastName"
                value={lastName}
                onChange={onChange}
                placeholder="Last Name"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Phone Number */}
        <Form.Item label="Phone Number" htmlFor="phoneNumber">
          <PhoneInput
            value={phoneNumber}
            onChange={(v) =>
              onChange({ target: { name: "phoneNumber", value: v } } as any)
            }
          />
        </Form.Item>
      </Form>

      <Divider />

      {/* Submit Button */}
      <Button
        disabled={isLoading || !phoneNumber || !firstName || !lastName || !note}
        onClick={onSendRequest}
        loading={isLoading}
        type="primary"
      >
        Request
      </Button>
    </>
  );
};

export default React.memo(RequestForm);
